import React, { useState, useEffect } from 'react';
import { COVER_BG_IMAGE } from '../constants/cover';
import { matchesAPI, teamsAPI } from '../services/api';
import { FaTrophy, FaArrowUp, FaArrowDown, FaMinus, FaFilter } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useLanguage } from '../context/LanguageContext';

const PublicPage = () => {
  const { language } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsRes, matchesRes] = await Promise.all([
        teamsAPI.getAll(),
        matchesAPI.getAll()
      ]);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
      calculateStandings(teamsRes.data, matchesRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStandings = (teamsData, matchesData) => {
    const teamStats = {};
    
    // Initialiser les stats pour chaque équipe
    teamsData.forEach(team => {
      if (!team.group_number) return;
      
      teamStats[team.id] = {
        id: team.id,
        name: team.name,
        logo_url: team.logo_url,
        group: team.group_number,
        played: 0,
        won: 0,
        drawn: 0,
        lost: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
        points: 0,
        form: []
      };
    });

    // Calculer les stats à partir des matchs terminés
    matchesData
      .filter(m => m.status === 'finished')
      .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
      .forEach(match => {
        const homeTeam = teamStats[match.home_team_id];
        const awayTeam = teamStats[match.away_team_id];

        if (homeTeam && awayTeam) {
          homeTeam.played++;
          awayTeam.played++;
          homeTeam.goalsFor += match.home_score;
          homeTeam.goalsAgainst += match.away_score;
          awayTeam.goalsFor += match.away_score;
          awayTeam.goalsAgainst += match.home_score;

          let homeResult, awayResult;
          if (match.home_score > match.away_score) {
            homeTeam.won++;
            homeTeam.points += 3;
            awayTeam.lost++;
            homeResult = 'W';
            awayResult = 'L';
          } else if (match.home_score < match.away_score) {
            awayTeam.won++;
            awayTeam.points += 3;
            homeTeam.lost++;
            homeResult = 'L';
            awayResult = 'W';
          } else {
            homeTeam.drawn++;
            awayTeam.drawn++;
            homeTeam.points += 1;
            awayTeam.points += 1;
            homeResult = 'D';
            awayResult = 'D';
          }

          homeTeam.form.push(homeResult);
          if (homeTeam.form.length > 5) homeTeam.form.shift();
          
          awayTeam.form.push(awayResult);
          if (awayTeam.form.length > 5) awayTeam.form.shift();

          homeTeam.goalDifference = homeTeam.goalsFor - homeTeam.goalsAgainst;
          awayTeam.goalDifference = awayTeam.goalsFor - awayTeam.goalsAgainst;
        }
      });

    // Organiser par groupe et trier
    const standingsByGroup = {};
    Object.values(teamStats).forEach(team => {
      const group = team.group;
      if (!standingsByGroup[group]) {
        standingsByGroup[group] = [];
      }
      standingsByGroup[group].push(team);
    });

    // Trier chaque groupe
    Object.keys(standingsByGroup).forEach(group => {
      standingsByGroup[group].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
        return b.goalsFor - a.goalsFor;
      });
    });

    setStandings(standingsByGroup);
  };

  const getFormIcon = (result) => {
    switch(result) {
      case 'W': return { icon: '●', color: '#28a745', label: language === 'ar' ? 'فوز' : 'Victoire' };
      case 'D': return { icon: '●', color: '#ffc107', label: language === 'ar' ? 'تعادل' : 'Nul' };
      case 'L': return { icon: '●', color: '#dc3545', label: language === 'ar' ? 'خسارة' : 'Défaite' };
      default: return { icon: '○', color: '#ccc', label: 'N/A' };
    }
  };

  const getPositionIndicator = (position, totalTeams) => {
    if (position === 1) return { icon: <FaArrowUp />, color: '#28a745' };
    if (position === 2) return { icon: <FaArrowUp />, color: '#17a2b8' };
    if (position === totalTeams) return { icon: <FaArrowDown />, color: '#dc3545' };
    return { icon: <FaMinus />, color: '#6c757d' };
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const groups = Object.keys(standings).sort((a, b) => a - b);

  const text = {
    ar: {
      title: 'الترتيب',
      subtitle: 'ترتيب الفرق حسب المجموعات',
      allGroups: 'جميع المجموعات',
      group: 'المجموعة',
      position: 'المركز',
      team: 'الفريق',
      played: 'لعب',
      won: 'فوز',
      drawn: 'تعادل',
      lost: 'خسارة',
      goalsFor: 'له',
      goalsAgainst: 'عليه',
      goalDiff: 'الفارق',
      points: 'النقاط',
      form: 'الشكل',
      teams: 'فريق',
      matches: 'مباراة',
      promotion: 'صعود',
      relegation: 'هبوط',
      victory: 'فوز',
      draw: 'تعادل',
      defeat: 'خسارة'
    },
    fr: {
      title: 'Classement',
      subtitle: 'Classement des équipes par groupe',
      allGroups: 'Tous les groupes',
      group: 'Groupe',
      position: 'Pos',
      team: 'Équipe',
      played: 'J',
      won: 'V',
      drawn: 'N',
      lost: 'D',
      goalsFor: 'BP',
      goalsAgainst: 'BC',
      goalDiff: 'Diff',
      points: 'Pts',
      form: 'Forme',
      teams: 'équipes',
      matches: 'matchs',
      promotion: 'Promotion',
      relegation: 'Relégation',
      victory: 'Victoire',
      draw: 'Nul',
      defeat: 'Défaite'
    }
  };

  const t = text[language];

  return (
    <div style={styles.container}>
      {/* Header */}
      <section style={styles.header}>
        <h1 style={styles.title}>{t.title}</h1>
        <p style={styles.subtitle}>{t.subtitle}</p>
        <div style={styles.statsBar}>
          <div style={styles.stat}>
            <span style={styles.statNumber}>{teams.filter(t => t.group_number).length}</span>
            <span style={styles.statLabel}>{t.teams}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statNumber}>{matches.filter(m => m.status === 'finished').length}</span>
            <span style={styles.statLabel}>{t.matches}</span>
          </div>
        </div>
      </section>

      {/* Filtres par Groupe */}
      <section style={styles.filterSection}>
        <div style={styles.filterContainer}>
          <div style={styles.filterLabel}>
            <FaFilter style={styles.filterIcon} />
            <span>{language === 'ar' ? 'تصفية حسب المجموعة' : 'Filtrer par groupe'}</span>
          </div>
          <div style={styles.filterButtons}>
            <button
              onClick={() => setSelectedGroup('all')}
              style={{
                ...styles.filterBtn,
                ...(selectedGroup === 'all' ? styles.filterBtnActive : {})
              }}
            >
              {t.allGroups}
            </button>
            {groups.map(group => (
              <button
                key={group}
                onClick={() => setSelectedGroup(group)}
                style={{
                  ...styles.filterBtn,
                  ...(selectedGroup === group ? styles.filterBtnActive : {})
                }}
              >
                {t.group} {group}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tableaux de Classement */}
      <section style={styles.standingsSection}>
        {groups.length === 0 ? (
          <div style={styles.emptyState}>
            <FaTrophy style={styles.emptyIcon} />
            <h3>{language === 'ar' ? 'لا يوجد ترتيب متاح' : 'Aucun classement disponible'}</h3>
          </div>
        ) : groups
          .filter(group => selectedGroup === 'all' || selectedGroup === group)
          .map(group => (
            <div key={group} style={styles.groupCard}>
              <div style={styles.groupHeader}>
                <h2 style={styles.groupTitle}>
                  {t.group} {group}
                </h2>
                <span style={styles.groupInfo}>
                  {standings[group].length} {t.teams} • {standings[group].reduce((sum, t) => sum + t.played, 0)} {t.matches}
                </span>
              </div>

              <div className="table-responsive" style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={{...styles.th, width: '50px'}}>{t.position}</th>
                      <th style={{...styles.th, width: '50px'}}></th>
                      <th style={{...styles.th, textAlign: language === 'ar' ? 'right' : 'left'}}>{t.team}</th>
                      <th style={styles.th}>{t.played}</th>
                      <th style={styles.th}>{t.won}</th>
                      <th style={styles.th}>{t.drawn}</th>
                      <th style={styles.th}>{t.lost}</th>
                      <th style={styles.th}>{t.goalsFor}</th>
                      <th style={styles.th}>{t.goalsAgainst}</th>
                      <th style={styles.th}>{t.goalDiff}</th>
                      <th style={{...styles.th, fontWeight: 'bold'}}>{t.points}</th>
                      <th style={styles.th}>{t.form}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings[group].map((team, index) => {
                      const posIndicator = getPositionIndicator(index + 1, standings[group].length);
                      return (
                        <tr 
                          key={team.id}
                          style={{
                            ...styles.tableRow,
                            ...(index === 0 ? styles.firstPlace : {}),
                            ...(index === 1 ? styles.secondPlace : {}),
                            ...(index === standings[group].length - 1 ? styles.lastPlace : {})
                          }}
                        >
                          <td style={styles.td}>
                            <span style={styles.positionNumber}>{index + 1}</span>
                          </td>
                          <td style={styles.td}>
                            <div style={{...styles.positionIndicator, color: posIndicator.color}}>
                              {posIndicator.icon}
                            </div>
                          </td>
                          <td style={{...styles.td, textAlign: language === 'ar' ? 'right' : 'left'}}>
                            <div style={styles.teamCell}>
                              {team.logo_url ? (
                                <img 
                                  src={team.logo_url}
                                  alt={team.name}
                                  style={styles.teamLogo}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div style={styles.noLogo}>
                                  {team.name.charAt(0)}
                                </div>
                              )}
                              <span style={styles.teamName}>{team.name}</span>
                            </div>
                          </td>
                          <td style={styles.td}>{team.played}</td>
                          <td style={{...styles.td, color: '#28a745', fontWeight: 'bold'}}>{team.won}</td>
                          <td style={{...styles.td, color: '#ffc107', fontWeight: 'bold'}}>{team.drawn}</td>
                          <td style={{...styles.td, color: '#dc3545', fontWeight: 'bold'}}>{team.lost}</td>
                          <td style={styles.td}>{team.goalsFor}</td>
                          <td style={styles.td}>{team.goalsAgainst}</td>
                          <td style={{
                            ...styles.td,
                            color: team.goalDifference > 0 ? '#28a745' : 
                                   team.goalDifference < 0 ? '#dc3545' : '#666',
                            fontWeight: 'bold'
                          }}>
                            {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                          </td>
                          <td style={{...styles.td, fontWeight: 'bold', fontSize: '16px', color: '#c41e3a'}}>
                            {team.points}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.formCell}>
                              {team.form.map((result, i) => {
                                const formData = getFormIcon(result);
                                return (
                                  <span
                                    key={i}
                                    style={{...styles.formDot, color: formData.color}}
                                    title={formData.label}
                                  >
                                    {formData.icon}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Légende */}
              <div style={styles.legend}>
                <div style={styles.legendItem}>
                  <div style={{...styles.legendBox, background: '#d4edda'}}></div>
                  <span>{t.promotion}</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{...styles.legendBox, background: '#f8d7da'}}></div>
                  <span>{t.relegation}</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{color: '#28a745', fontSize: '18px'}}>●</span>
                  <span>{t.victory}</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{color: '#ffc107', fontSize: '18px'}}>●</span>
                  <span>{t.draw}</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{color: '#dc3545', fontSize: '18px'}}>●</span>
                  <span>{t.defeat}</span>
                </div>
              </div>
            </div>
          ))}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  header: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '80px 20px 60px',
    textAlign: 'center',
    color: 'white'
  },
  title: {
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '15px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
  },
  subtitle: {
    fontSize: '20px',
    marginBottom: '30px',
    opacity: 0.9
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginTop: '30px',
    flexWrap: 'wrap'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.2)',
    padding: '20px 40px',
    borderRadius: '15px',
    backdropFilter: 'blur(10px)'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: 'bold'
  },
  statLabel: {
    fontSize: '16px',
    marginTop: '5px'
  },
  filterSection: {
    background: 'white',
    padding: '30px 20px',
    borderBottom: '2px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
  },
  filterContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333'
  },
  filterIcon: {
    color: '#c41e3a',
    fontSize: '18px'
  },
  filterButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  filterBtn: {
    padding: '10px 20px',
    background: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  filterBtnActive: {
    background: '#c41e3a',
    color: 'white',
    borderColor: '#c41e3a'
  },
  standingsSection: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '60px 20px'
  },
  groupCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    marginBottom: '30px'
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '2px solid #c41e3a',
    flexWrap: 'wrap',
    gap: '10px'
  },
  groupTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0
  },
  groupInfo: {
    fontSize: '14px',
    color: '#666'
  },
  tableWrapper: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    width: '100%'
  },
  table: {
    width: '100%',
    minWidth: '600px',
    borderCollapse: 'collapse'
  },
  tableHeader: {
    background: '#f8f9fa',
    borderBottom: '2px solid #dee2e6'
  },
  th: {
    padding: '12px 8px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase'
  },
  tableRow: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.2s'
  },
  firstPlace: {
    background: 'rgba(212, 237, 218, 0.3)'
  },
  secondPlace: {
    background: 'rgba(212, 237, 218, 0.15)'
  },
  lastPlace: {
    background: 'rgba(248, 215, 218, 0.3)'
  },
  td: {
    padding: '12px 8px',
    textAlign: 'center',
    fontSize: '14px',
    color: '#333'
  },
  positionNumber: {
    fontWeight: 'bold',
    fontSize: '15px'
  },
  positionIndicator: {
    fontSize: '16px'
  },
  teamCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  teamLogo: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
    flexShrink: 0
  },
  noLogo: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#c41e3a',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
    flexShrink: 0
  },
  teamName: {
    fontWeight: '600',
    fontSize: '14px',
    flex: 1
  },
  formCell: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'center'
  },
  formDot: {
    fontSize: '16px',
    cursor: 'help'
  },
  legend: {
    display: 'flex',
    gap: '20px',
    marginTop: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px'
  },
  legendBox: {
    width: '20px',
    height: '20px',
    borderRadius: '4px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
    color: '#999'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
    opacity: 0.3
  }
};

// Ajouter l'animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  .tableRow:hover {
    background: rgba(196, 30, 58, 0.05) !important;
  }

  .filterBtn:hover {
    border-color: #c41e3a;
    color: #c41e3a;
  }

  @media (max-width: 768px) {
    .filterButtons {
      flex-direction: column;
    }
    
    .filterBtn {
      width: 100%;
    }
    
    .tableWrapper {
      overflow-x: scroll;
    }
  }
`;
document.head.appendChild(styleSheet);

export default PublicPage;
